import { useCallback, useEffect, useState } from 'react';
import { Toggle } from 'konsta/react';
import { Clock, MessageCircle, X } from 'lucide-react';
import { CANCEL_REASONS } from '@harmony/shared';
import { api, type Booking, type BookingStatus, type MyProviderProfile } from '../api';
import { Avatar, BottomSheet, EmptyState, PrimaryButton, etb, timeLabel, useToast } from '../components/ui';
import { Loading } from './client/Discover';
import { FeedbackCards } from './Growth';
import { getSocket } from '../realtime';
import { haptic } from '../telegram';
import { useErrorText, useLang, useName, useT } from '../i18n';

const STATUS_STYLE: Record<BookingStatus, string> = {
  PENDING: 'bg-amber-500/15 text-amber-700',
  ACCEPTED: 'bg-green-500/15 text-green-700',
  COMPLETED: 'bg-green-500/15 text-green-700',
  DECLINED: 'bg-red-500/10 text-red-600',
  EXPIRED: 'bg-tg-secondary text-tg-hint',
  CANCELLED_BY_CLIENT: 'bg-tg-secondary text-tg-hint',
  CANCELLED_BY_PROVIDER: 'bg-red-500/10 text-red-600',
  DISPUTED: 'bg-red-500/10 text-red-600',
  CLOSED: 'bg-tg-secondary text-tg-hint',
};
const ACTIVE: BookingStatus[] = ['PENDING', 'ACCEPTED'];

export function StatusBadge({ s }: { s: BookingStatus }) {
  const t = useT();
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLE[s]}`}>{t(`status.${s}`)}</span>;
}

function minutesLeft(iso: string) {
  return Math.max(0, Math.ceil((Date.parse(iso) - Date.now()) / 60_000));
}

/** Refreshes a list when something changes elsewhere (a reply, a cancellation, a new message). */
function useLiveReload(load: () => void) {
  useEffect(() => {
    load();
    const s = getSocket();
    const id = window.setInterval(load, 30_000);
    const onAny = () => load();
    s?.on('chat:message', onAny);
    document.addEventListener('visibilitychange', onAny);
    return () => {
      window.clearInterval(id);
      s?.off('chat:message', onAny);
      document.removeEventListener('visibilitychange', onAny);
    };
  }, [load]);
}

/** Client: requests they sent, with Cancel and Open chat. */
export function ClientBookingsTab({ openChat }: { openChat: (conversationId: string) => void }) {
  const t = useT();
  const lang = useLang();
  const [items, setItems] = useState<Booking[] | null>(null);
  const [tab, setTab] = useState<'active' | 'past'>('active');
  const [cancelling, setCancelling] = useState<Booking | null>(null);
  const load = useCallback(() => {
    api.myBookings().then(setItems).catch(() => undefined);
  }, []);
  useLiveReload(load);

  if (!items) return <Loading />;
  const shown = items.filter((b) => (tab === 'active') === ACTIVE.includes(b.status));
  return (
    <div className="pb-28">
      <Segments value={tab} onChange={setTab} options={[['active', t('bookings.active')], ['past', t('bookings.past')]]} />
      {tab === 'active' && <FeedbackCards />}
      {!shown.length && <EmptyState icon="📅" text={t('bookings.empty')} />}
      <div className="space-y-2 px-3">
        {shown.map((b) => (
          <div key={b.id} className="rounded-2xl bg-tg-secondary p-3">
            <div className="flex items-center gap-3">
              <Avatar src={b.provider?.photo} name={b.provider?.name ?? ''} size={48} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-semibold">{b.provider?.name}</span>
                  <StatusBadge s={b.status} />
                </div>
                <div className="text-sm text-tg-hint">
                  {t('booking.session', { minutes: b.minutes, price: etb(b.price) })} · {timeLabel(b.requestedAt, lang)}
                </div>
                {b.status === 'PENDING' && (
                  <div className="mt-0.5 flex items-center gap-1 text-xs text-amber-700">
                    <Clock size={12} /> {t('booking.expires_in', { m: minutesLeft(b.expiresAt) })}
                  </div>
                )}
              </div>
            </div>
            {(b.status === 'ACCEPTED' || b.status === 'PENDING') && (
              <div className="mt-3 flex gap-2">
                {b.conversationId && b.status === 'ACCEPTED' && (
                  <button onClick={() => openChat(b.conversationId!)} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-tg-button py-2.5 text-sm font-semibold text-tg-buttonText">
                    <MessageCircle size={16} /> {t('booking.open_chat')}
                  </button>
                )}
                <button onClick={() => setCancelling(b)} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-tg-bg py-2.5 text-sm font-semibold text-red-600">
                  <X size={16} /> {t('booking.cancel')}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
      <CancelSheet booking={cancelling} asClient onClose={() => setCancelling(null)} onDone={load} />
    </div>
  );
}

function CancelSheet({ booking, asClient, onClose, onDone }: { booking: Booking | null; asClient: boolean; onClose: () => void; onDone: () => void }) {
  const t = useT();
  const toast = useToast();
  const errText = useErrorText();
  const [busy, setBusy] = useState(false);
  const reasons = asClient ? CANCEL_REASONS.filter((r) => r !== 'PROVIDER_CANCELED') : (['PROVIDER_CANCELED'] as const);
  const cancel = async (reason: string) => {
    if (!booking) return;
    setBusy(true);
    try {
      await api.cancelBooking(booking.id, reason);
      haptic('success');
      onClose();
      onDone();
    } catch (e) {
      toast(errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <BottomSheet open={!!booking} onClose={onClose}>
      <h3 className="mb-3 text-lg font-bold">{asClient ? t('booking.cancel_title') : t('booking.cancel')}</h3>
      <div className="space-y-2">
        {reasons.map((r) => (
          <PrimaryButton key={r} tone={asClient ? 'soft' : 'danger'} disabled={busy} onClick={() => cancel(r)}>
            {asClient ? t(`cancel.${r}`) : t('booking.cancel')}
          </PrimaryButton>
        ))}
        <PrimaryButton tone="soft" onClick={onClose}>
          {t('common.close')}
        </PrimaryButton>
      </div>
    </BottomSheet>
  );
}

/** Provider: availability switch on top, then new requests (Accept/Decline) and accepted bookings. */
export function ProviderRequestsTab({ openChat, onPendingCount }: { openChat: (conversationId: string) => void; onPendingCount?: (n: number) => void }) {
  const t = useT();
  const lang = useLang();
  const name = useName();
  const toast = useToast();
  const errText = useErrorText();
  const [items, setItems] = useState<Booking[] | null>(null);
  const [me, setMe] = useState<MyProviderProfile | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<Booking | null>(null);

  const load = useCallback(() => {
    api.incoming().then((r) => {
      setItems(r);
      onPendingCount?.(r.filter((b) => b.status === 'PENDING' && Date.parse(b.expiresAt) > Date.now()).length);
    }).catch(() => undefined);
    api.myProviderProfile().then(setMe).catch(() => undefined);
  }, []);
  useLiveReload(load);

  const answer = async (b: Booking, accept: boolean) => {
    setBusy(b.id);
    try {
      if (accept) {
        const r = await api.accept(b.id);
        haptic('success');
        openChat(r.conversationId);
      } else {
        await api.decline(b.id);
        haptic('success');
      }
    } catch (e) {
      haptic('error');
      toast(errText(e));
    } finally {
      setBusy(null);
      load();
    }
  };

  const toggle = async () => {
    if (!me) return;
    const next = !me.available;
    setMe({ ...me, available: next });
    haptic('select');
    try {
      await api.setAvailability(next);
    } catch (e) {
      setMe({ ...me, available: !next });
      toast(errText(e));
    }
  };

  if (!items) return <Loading />;
  const pending = items.filter((b) => b.status === 'PENDING' && Date.parse(b.expiresAt) > Date.now());
  const accepted = items.filter((b) => b.status === 'ACCEPTED');
  return (
    <div className="pb-28">
      {me && (
        <div className={`m-3 flex items-center gap-3 rounded-2xl p-4 ${me.available ? 'bg-green-500/15' : 'bg-tg-secondary'}`}>
          <div className="flex-1">
            <div className="font-semibold">{t('availability.label')}</div>
            <div className="text-sm text-tg-hint">
              {me.approvalStatus !== 'APPROVED' ? t('availability.pending') : me.available ? t('availability.on') : t('availability.off')}
            </div>
          </div>
          <Toggle checked={me.available} disabled={me.approvalStatus !== 'APPROVED'} onChange={toggle} />
        </div>
      )}

      <h2 className="px-4 pb-2 pt-2 text-sm font-semibold text-tg-hint">{t('requests.new')}</h2>
      {!pending.length && <p className="px-4 pb-4 text-tg-hint">{t('requests.empty')}</p>}
      <div className="space-y-2 px-3">
        {pending.map((b) => (
          <div key={b.id} className="rounded-2xl border-2 border-amber-400/60 bg-tg-bg p-3">
            <div className="flex items-center gap-3">
              <Avatar name={b.client?.name ?? ''} size={48} />
              <div className="min-w-0 flex-1">
                <div className="font-semibold">{b.client?.name}</div>
                <div className="text-sm text-tg-hint">
                  {[b.client?.gender ? t(`gender.${b.client.gender}`) : null, name(b.client?.area) || name(b.client?.city)].filter(Boolean).join(' · ')}
                </div>
                <div className="text-sm font-medium">{t('booking.session', { minutes: b.minutes, price: etb(b.price) })}</div>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-1 text-xs text-amber-700">
              <Clock size={12} /> {t('booking.expires_in', { m: minutesLeft(b.expiresAt) })}
            </div>
            <div className="mt-3 flex gap-2">
              <button disabled={busy === b.id} onClick={() => answer(b, false)} className="flex-1 rounded-xl bg-tg-secondary py-3 font-semibold text-red-600 disabled:opacity-50">
                {t('requests.decline')}
              </button>
              <button disabled={busy === b.id} onClick={() => answer(b, true)} className="flex-[2] rounded-xl bg-green-600 py-3 font-semibold text-white disabled:opacity-50">
                {t('requests.accept')}
              </button>
            </div>
          </div>
        ))}
      </div>

      {!!accepted.length && <h2 className="px-4 pb-2 pt-5 text-sm font-semibold text-tg-hint">{t('requests.accepted')}</h2>}
      <div className="space-y-2 px-3">
        {accepted.map((b) => (
          <div key={b.id} className="flex items-center gap-3 rounded-2xl bg-tg-secondary p-3">
            <Avatar name={b.client?.name ?? ''} size={44} />
            <div className="min-w-0 flex-1">
              <div className="font-semibold">{b.client?.name}</div>
              <div className="text-sm text-tg-hint">
                {t('booking.session', { minutes: b.minutes, price: etb(b.price) })} · {timeLabel(b.acceptedAt ?? b.requestedAt, lang)}
              </div>
            </div>
            {b.conversationId && (
              <button onClick={() => openChat(b.conversationId!)} className="rounded-full bg-tg-button p-2.5 text-tg-buttonText" aria-label="chat">
                <MessageCircle size={18} />
              </button>
            )}
            <button onClick={() => setCancelling(b)} className="rounded-full bg-tg-bg p-2.5 text-red-600" aria-label="cancel">
              <X size={18} />
            </button>
          </div>
        ))}
      </div>
      <CancelSheet booking={cancelling} asClient={false} onClose={() => setCancelling(null)} onDone={load} />
    </div>
  );
}

function Segments<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: Array<[T, string]> }) {
  return (
    <div className="m-3 flex rounded-xl bg-tg-secondary p-1">
      {options.map(([v, label]) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          className={`flex-1 rounded-lg py-2 text-sm font-semibold ${value === v ? 'bg-tg-bg shadow-sm' : 'text-tg-hint'}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
